"use client";

import { usePathname } from "next/navigation";
import React, {
  useEffect,
  useRef,
  type ComponentProps,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import { Link } from "../i18n/navigation";

type HeaderNavigationLinkProps = Omit<
  ComponentProps<typeof Link>,
  "onNavigate" | "scroll"
>;

export function HeaderNavigationLink(props: HeaderNavigationLinkProps) {
  return <Link {...props} scroll={false} />;
}

export function HeaderNavigation({
  children,
  className,
}: {
  readonly children: ReactNode;
  readonly className: string;
}) {
  const pathname = usePathname();
  const previousPathnameRef = useRef(pathname);
  const resetAfterNavigationRef = useRef(false);

  useEffect(() => {
    if (previousPathnameRef.current === pathname) {
      return;
    }

    previousPathnameRef.current = pathname;
    if (resetAfterNavigationRef.current) {
      resetAfterNavigationRef.current = false;
      let finalFrame: number | undefined;
      const routeFrame = window.requestAnimationFrame(() => {
        finalFrame = window.requestAnimationFrame(() => {
          window.scrollTo({ top: 0, left: 0, behavior: "instant" });
        });
      });
      return () => {
        window.cancelAnimationFrame(routeFrame);
        if (finalFrame !== undefined) {
          window.cancelAnimationFrame(finalFrame);
        }
      };
    }
  }, [pathname]);

  const markNavigation = (event: ReactMouseEvent<HTMLElement>) => {
    if (
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      !(event.target instanceof Element) ||
      event.target.closest("a[href]") === null
    ) {
      return;
    }

    resetAfterNavigationRef.current = true;
  };

  return (
    <header className={className} onClick={markNavigation}>
      {children}
    </header>
  );
}
